import { useEffect, useState, useMemo, useCallback, useRef } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { LoggedInHTTPError } from "@impero-com/common/errorPages/loggedInHTTPError";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { canGoBack } from "@impero-com/common/utils/url";
import {
    Button,
    emitNotification,
    InformationModal,
    PageSpinner,
} from "@impero-com/ui";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate, useLocation } from "react-router-dom";

import {
    ControlWithFieldsResources,
    NewControlWithFieldsView,
    DataSheetAssignmentsListDto,
} from "api/generated/model";
import { Field } from "components/fieldBuilder/models";
import { useLastPresent } from "components/modals/useLastPresent";
import { buildControlProgramViewUrl } from "org/control/controlProgram/urlUtils";

import { WebRoute } from "../../../routes";
import { useOptionalControlProgramQuery } from "../controlProgram/queries/useControlProgramQuery";

import { ControlViewForm } from "./ControlViewForm";
import {
    applyControlPolicies,
    toCreateControlPayload,
    toControlViewFormState,
    useDataSheetAssignmentsForCurrentControl,
    ControlViewFormState,
} from "./formData";
import {
    useControlViewFormSchema,
    toRHFDefaultValues,
    rhfValuesToControlViewFormState,
    ControlViewFormSchema,
} from "./formSchema";
import { ControlPageWrapper, ControlSubmitType } from "./pageWrapper";
import { useCPDatasheetAssignmentsQuery } from "./queries/useCPDatasheetAssignmentsQuery";
import { useCreateControlMutation } from "./queries/useCreateControlMutation";
import { useNewControlQuery } from "./queries/useNewControlQuery";

interface CreateControlViewProps {
    controlView: NewControlWithFieldsView;
    controlResources: ControlWithFieldsResources;
    isCopy: boolean;
}
const defaultDataSheetAssignments: DataSheetAssignmentsListDto = {
    content: [],
    linkedResources: { controls: [], dataSheets: [] },
};

const CreateControlView = ({
    controlView,
    controlResources,
    isCopy,
}: CreateControlViewProps) => {
    const { t } = useTranslation();

    const controlId = controlView.id;

    const baseFormState = useMemo(
        () =>
            applyControlPolicies(
                toControlViewFormState(controlView),
                controlResources.controlPolicies
            ),
        [controlView, controlResources.controlPolicies]
    );
    const triggersControls = false;
    // Live ref so the schema's DataSheet existence/grouping check sees fresh
    // assignments data once the query resolves (schema is built once, before
    // the controlProgramId-driven query has anything to return).
    const dataSheetAssignmentsRef = useRef<DataSheetAssignmentsListDto>(
        defaultDataSheetAssignments
    );
    const schema = useControlViewFormSchema(
        triggersControls,
        dataSheetAssignmentsRef
    );
    const rhfForm = useForm<ControlViewFormSchema>({
        resolver: zodResolver(schema),
        mode: "onBlur",
        defaultValues: toRHFDefaultValues(baseFormState),
    });

    const controlProgramId = useWatch({
        control: rhfForm.control,
        name: "controlProgramId",
    });
    const sendImmediately = useWatch({
        control: rhfForm.control,
        name: "sendImmediately",
    });
    const responsibleTasks = useWatch({
        control: rhfForm.control,
        name: "responsibleTasks",
    });
    const formDirty = rhfForm.formState.isDirty;
    // A copy opens as a complete control, so it can be created as is
    const submitDisabled = !formDirty && !isCopy;

    const { data: controlProgramData } =
        useOptionalControlProgramQuery(controlProgramId);

    const linkedEntities = controlProgramData?.linkedResources.entities ?? [];

    // DataSheet assignments derived from RHF values
    const { data: dataSheetAssignmentsData } =
        useCPDatasheetAssignmentsQuery(controlProgramId);
    const dataSheetAssignments = useDataSheetAssignmentsForCurrentControl(
        responsibleTasks,
        dataSheetAssignmentsData ?? defaultDataSheetAssignments
    );
    useEffect(() => {
        dataSheetAssignmentsRef.current =
            dataSheetAssignmentsData ?? defaultDataSheetAssignments;
    }, [dataSheetAssignmentsData]);

    // Analytics tracking (not in schema — merged at submit time)
    const analyticsRef = useRef<Record<string, Field<unknown, string>[]>>({});
    const onAnalytics = useCallback(
        (suggestionId: string, fields: Field<unknown, string>[]) => {
            analyticsRef.current = {
                ...analyticsRef.current,
                [suggestionId]: [
                    ...(analyticsRef.current[suggestionId] ?? []),
                    ...fields,
                ],
            };
        },
        []
    );

    const { mutateAsync: createControl } = useCreateControlMutation();
    const navigate = useNavigate();

    const validateForm = useCallback(
        () =>
            new Promise<ControlViewFormState | null>((resolve) => {
                void rhfForm.handleSubmit(
                    (data) => {
                        const state = rhfValuesToControlViewFormState(
                            data,
                            baseFormState
                        );
                        state.analytics = analyticsRef.current;
                        resolve(state);
                    },
                    (errors) => {
                        console.error("ControlViewForm validation errors", {
                            errors,
                            values: rhfForm.getValues(),
                        });
                        resolve(null);
                    }
                )();
            }),
        [rhfForm, baseFormState]
    );

    const handleSubmitConfirm = async (type: ControlSubmitType) => {
        const values = await validateForm();
        if (values === null) {
            emitNotification(
                "warning",
                t("CreateControl_Submit_ValidationError")
            );
            throw new Error("Validation error");
        }
        const data = toCreateControlPayload(values);
        const created = await createControl({
            data,
            params: {
                create_new_control_results: type === "send",
                send_immediately:
                    type === "send" && sendImmediately ? true : undefined,
            },
        });
        window.location.href = buildControlProgramViewUrl(controlProgramId!, {
            controlId: created.data.id,
        });
    };

    const handleCancel = () => {
        if (canGoBack(history)) {
            navigate(-1);
        } else {
            navigate(WebRoute.CONTROLS_ALL);
        }
    };

    return (
        <ControlPageWrapper
            onSubmitClick={handleSubmitConfirm}
            onCancel={handleCancel}
            validateForm={validateForm}
            submitDisabled={submitDisabled}
            submitLabel={t("CreateControl_CreateTitle")}
            titleLabel={t("CreateControl_CreateTitle")}
            controlId={controlId}
            controlProgramId={controlProgramId}
            formDirty={formDirty}
            pageType="create"
            sendImmediately={sendImmediately}
            alteredTriggeredControlCount={0}
        >
            <ControlViewForm
                control={rhfForm.control}
                getValues={rhfForm.getValues}
                setValue={rhfForm.setValue}
                trigger={rhfForm.trigger}
                onAnalytics={onAnalytics}
                controlResources={controlResources}
                controlId={controlId}
                dataSheetAssignments={dataSheetAssignments}
                notificationRecipients={{}}
                initialTriggeredByControlId={controlView.triggeredBy ?? null}
                hasFullAssignmentsAccess={controlProgramId !== null}
                linkedEntities={linkedEntities}
            />
        </ControlPageWrapper>
    );
};

type ParamErrorVariant = "sourceControlId" | "controlTemplateId";
interface ParamErrorDialogProps {
    errorVariant: ParamErrorVariant | undefined;
    onCancel: () => void;
}
const ParamErrorDialog = ({
    errorVariant,
    onCancel,
}: ParamErrorDialogProps) => {
    const { t } = useTranslation();
    // Keeps the message in place while the modal animates closed.
    const shown = useLastPresent(errorVariant);
    const paramErrorVariantMap: Record<ParamErrorVariant, string> = {
        sourceControlId: t("CreateControl_WarnNoMatchQuerySource"),
        controlTemplateId: t("CreateControl_WarnNoMatchQueryTemplate"),
    };
    return (
        <InformationModal
            open={Boolean(errorVariant)}
            onClose={onCancel}
            type="warning"
            title={t("CreateControl_WarnTitleNoMatchQuery")}
            footer={
                <Button variant="primary" onClick={onCancel}>
                    {t("Close")}
                </Button>
            }
        >
            {shown && paramErrorVariantMap[shown]}
        </InformationModal>
    );
};

const CONTROL_PROGRAM_ID_SEARCH_PARAM = "control_program_id";
const CONTROL_TEMPLATE_ID_SEARCH_PARAM = "control_template_id";
const SOURCE_CONTROL_ID_SEARCH_PARAM = "source_control_id";

const CreateControlPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { search } = useLocation();
    const params = useMemo(() => new URLSearchParams(search), [search]);
    const [paramErrorVariant, setParamErrorVariant] = useState<
        ParamErrorVariant | undefined
    >();

    const controlProgramIdParam =
        Number(params.get(CONTROL_PROGRAM_ID_SEARCH_PARAM)) || undefined;
    const controlTemplateIdParam =
        Number(params.get(CONTROL_TEMPLATE_ID_SEARCH_PARAM)) || undefined;
    const sourceControlIdParam =
        params.get(SOURCE_CONTROL_ID_SEARCH_PARAM) || undefined;

    const {
        data: newControlData,
        status: newControlQueryStatus,
        error: newControlError,
    } = useNewControlQuery({
        controlProgramId: controlProgramIdParam,
        controlTemplateId: controlTemplateIdParam,
        sourceControlId: sourceControlIdParam,
    });

    const controlProgramId =
        newControlQueryStatus === "success" &&
        newControlData.content.resource.controlProgramId;

    // Removes invalid query param AFTER fetching newControlErrorStatus and warns
    useEffect(() => {
        const statusCode =
            newControlQueryStatus === "error" &&
            newControlError.response?.status;
        const updateParams = () =>
            navigate(
                {
                    pathname: window.location.pathname,
                    search: `?${params.toString()}`,
                },
                { replace: true }
            );
        if (statusCode === 404 && controlTemplateIdParam) {
            params.delete(CONTROL_TEMPLATE_ID_SEARCH_PARAM);
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setParamErrorVariant("controlTemplateId");
            updateParams();
        }
        if (statusCode === 400 && sourceControlIdParam) {
            params.delete(SOURCE_CONTROL_ID_SEARCH_PARAM);
            setParamErrorVariant("sourceControlId");
            updateParams();
        }
    }, [
        controlTemplateIdParam,
        navigate,
        params,
        sourceControlIdParam,
        newControlQueryStatus,
        newControlError,
    ]);

    // If no control program matching ID in query params is found, removes the param
    useEffect(() => {
        if (
            controlProgramIdParam && // The param id we get in the URL from the user
            !controlProgramId && // The valid id we get from the server
            newControlQueryStatus !== "pending"
        ) {
            emitNotification(
                "warning",
                t("CreateControl_WarnNoMatchQueryProgram"),
                {
                    duration: "DOUBLE",
                }
            );
            params.delete(CONTROL_PROGRAM_ID_SEARCH_PARAM);
            navigate(
                {
                    pathname: window.location.pathname,
                    search: `?${params.toString()}`,
                },
                { replace: true }
            );
        }
    }, [
        controlProgramIdParam,
        navigate,
        params,
        newControlQueryStatus,
        controlProgramId,
        t,
    ]);

    switch (newControlQueryStatus) {
        case "error":
            return (
                <LoggedInHTTPError code={newControlError.response?.status} />
            );
        case "pending":
            return <PageSpinner />;
        case "success":
            return (
                <>
                    <ParamErrorDialog
                        errorVariant={paramErrorVariant}
                        onCancel={() => setParamErrorVariant(undefined)}
                    />
                    <CreateControlView
                        controlResources={newControlData.linkedResources}
                        controlView={newControlData.content.resource}
                        isCopy={sourceControlIdParam !== undefined}
                    />
                </>
            );
        default:
            return assertNever(newControlQueryStatus);
    }
};

export { CreateControlPage, CreateControlView };
